export type QueryLabel =
  | 'phone'
  | 'location'
  | 'name'
  | 'parcel'
  | 'place'
  | 'badge';

export interface QueryClassification {
  labels: QueryLabel[];
  has(label: QueryLabel): boolean;
  normalizedPhone: string | null;
}

const PLACE_NAMES_LIST = [
  'hospital',
  'clinic',
  'school',
  'church',
  'library',
  'park',
  'station',
  'airport',
  'mall',
  'shelter',
  'pharmacy',
  'hydrant',
  'fire station',
  'police station',
  'post office',
  'city hall',
  'courthouse',
  'jail',
  'prison',
  'gym',
  'arena',
  'stadium',
  'theater',
  'theatre',
  'museum',
  'cemetery',
  'temple',
  'mosque',
  'synagogue',
  'hotel',
  'motel',
  'restaurant',
  'bar',
  'cafe',
  'bank',
  'atm',
  'gas station',
  'parking',
  'garage',
  'warehouse',
  'factory',
  'bridge',
  'dam',
  'tower',
  'pier',
  'marina',
  'beach',
  'pool',
  'lake',
  'river',
  'creek',
  'pond',
  'mountain',
  'hill',
  'valley',
  'forest',
  'campground',
  'trailhead',
  'overlook',
  'rest area',
  'rest stop',
  'truck stop',
  'urgent care',
  'emergency room',
  'er',
  'icu',
  'nursery',
  'daycare',
  'preschool',
  'elementary',
  'middle school',
  'high school',
  'university',
  'college',
  'community center',
  'recreation center',
  'senior center',
  'animal shelter',
  'veterinary',
  'vet',
  'landfill',
  'dump',
  'recycling center',
  'water treatment',
  'power plant',
  'substation',
];

const PLACE_NAMES_SINGLE: Record<string, true> = {};
const PLACE_NAMES_MULTI: string[] = [];
PLACE_NAMES_LIST.forEach(p => {
  PLACE_NAMES_SINGLE[p] = true;
  if (p.includes(' ')) PLACE_NAMES_MULTI.push(p);
});

function isPlaceName(lower: string): boolean {
  if (PLACE_NAMES_SINGLE[lower]) return true;
  return PLACE_NAMES_MULTI.some(p => lower.includes(p));
}

function normalizePhone(digits: string): string {
  const d =
    digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

export function classifyQuery(raw: string): QueryClassification {
  const text = raw.trim();
  if (!text) {
    return { labels: ['location'], has: () => false, normalizedPhone: null };
  }

  const lower = text.toLowerCase();
  const labels: QueryLabel[] = [];
  let normalizedPhone: string | null = null;

  const onlyDigitsAndDashes = /^[\d-]+$/.test(text);
  const digits = text.replace(/\D/g, '');
  const hasLetters = /[a-zA-Z]/.test(text);
  const hasDigits = digits.length > 0;
  const onlyLettersAndSpaces = /^[a-zA-Z\s]+$/.test(text);

  if (onlyDigitsAndDashes && digits.length > 0) {
    // pure numbers (with optional dashes stripped)
    if (digits.length === 10 || (digits.length === 11 && digits[0] === '1')) {
      labels.push('phone');
      normalizedPhone = normalizePhone(digits);
    }
    if (digits.length >= 12) {
      labels.push('parcel');
    }
    labels.push('badge');
    if (labels.length === 1 && labels[0] === 'badge') {
      labels.push('location');
    }
  } else if (onlyLettersAndSpaces) {
    labels.push('location');
    if (isPlaceName(lower)) labels.push('place');
    labels.push('name');
  } else if (hasLetters && hasDigits) {
    labels.push('location');
    if (isPlaceName(lower)) labels.push('place');
  } else {
    labels.push('location');
  }

  if (hasLetters && !labels.includes('location')) {
    labels.push('location');
  }

  const labelSet = new Set(labels);
  return {
    labels,
    has(label: QueryLabel) {
      return labelSet.has(label);
    },
    normalizedPhone,
  };
}
